import {
  BubbleChart,
  CloseRounded,
  FlashOnRounded,
  ForumRounded,
  NotificationsOutlined,
  NotificationsRounded,
  SchoolRounded
} from '@mui/icons-material'
import { Badge, Box, Button, Menu, Tabs, Tooltip, Typography } from '@mui/material'
import moment from 'moment'
import { MouseEvent, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import {
  getNotifications,
  getUnreadNotifications,
  setAllNotificationsAsRead,
  setNotificationAsRead
} from '../../../../redux/actions/notifications'
import { IRootState } from '../../../../redux/types'
import useTabState from '../../hooks/useTabState'
import ButtonNavbar from '../../styles/ButtonNavbar'
import HiddenDesktop from '../../styles/HiddenDesktop'
import HiddenMobile from '../../styles/HiddenMobile'
import {
  CleanButton,
  NotificationDrawerMobile,
  NotificationList,
  NotificationListItemButton,
  NotificationTab,
  StyledBadge
} from '../../styles/NotificationPanelStyle'
import TabPanel from '../TabPanel'

function NotificationPanel({ theme }: { theme: string }) {
  const { t } = useTranslation(['global'])
  const dispatch = useDispatch()

  const { notifications } = useSelector((state: IRootState) => state.notification)
  const { currentTab, setCurrentTab, a11yProps } = useTabState()
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const isMenuOpen = Boolean(anchorEl)
  const [showMenu, setShowMenu] = useState(true)
  const [mobileUserOpen, setMobileUserOpen] = useState(false)

  const handleProfileMenuOpen = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleChangeTab = (newValue: number) => {
    setCurrentTab(newValue)
    if (newValue === 0) {
      dispatch(getUnreadNotifications())
    }
    if (newValue === 1) {
      dispatch(getNotifications())
    }
  }

  const handleClickNotification = (notification: any) => {
    dispatch(setNotificationAsRead(notification.id))
    setAnchorEl(null)
  }

  const icons: any[string] = []
  icons['news'] = <ForumRounded fontSize="large" />
  icons['novelty'] = <FlashOnRounded fontSize="large" />
  icons['course'] = <SchoolRounded fontSize="large" />
  const typeDetail = (type: any) => {
    return icons[type]
  }

  return (
    <>
      <HiddenMobile>
        <Tooltip title={`${t('admin.people.notifications')}`}>
          <ButtonNavbar id="notifications-button" className={theme} onClick={handleProfileMenuOpen}>
            <Badge
              badgeContent={notifications.filter((notification: any) => notification.read_at === null).length}
              max={99}
              anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
              color={theme === 'client' ? 'primary' : 'secondary'}
            >
              <NotificationsRounded />
            </Badge>
          </ButtonNavbar>
        </Tooltip>
        <Menu
          data-testid="notification-panel"
          anchorEl={anchorEl}
          id="notification-panel"
          keepMounted
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          open={isMenuOpen}
          onClose={() => setAnchorEl(null)}
        >
          {showMenu && (
            <Box p={3} width={448}>
              <Box display="flex" alignItems="center">
                <Typography variant="h6" style={{ fontWeight: 500 }}>
                  {t('admin.people.notifications')}
                </Typography>
              </Box>
              <Box>
                <Tabs
                  value={currentTab}
                  indicatorColor="primary"
                  textColor="primary"
                  onChange={(_, value) => handleChangeTab(value)}
                  style={{ minHeight: 'fit-content' }}
                  aria-label="disabled tabs example"
                >
                  <NotificationTab data-testid="pending-notifications" label={t('admin.people.pending')} {...a11yProps(0)} />
                  <NotificationTab data-testid="all-notifications" label={t('all')} {...a11yProps(1)} />
                </Tabs>
              </Box>

              <TabPanel value={currentTab} index={0} noPadding={true}>
                {notifications.length === 0 ? (
                  <Box width="100%" gap={3} height={300} display="flex" alignItems="center" justifyContent="center" flexDirection="column">
                    <BubbleChart style={{ fontSize: '100px' }} htmlColor={`${import.meta.env.VITE_COLOR_PRIMARY_LIGHT || '#FD7296'}`} />
                    <Typography variant="subtitle1">{t('you-havent-notifications')}</Typography>
                    <Button variant="contained" size="small" color="primary" onClick={() => setAnchorEl(null)}>
                      {t('back')}
                    </Button>
                  </Box>
                ) : (
                  <>
                    <Box width="100%" display="flex" justifyContent="flex-end">
                      <CleanButton
                        onClick={() => {
                          dispatch(setAllNotificationsAsRead())
                        }}
                        variant="text"
                      >
                        {t('mark-all-as-read')}
                      </CleanButton>
                    </Box>
                    <NotificationList>
                      {notifications.map((notification: any) => (
                        <NotificationListItemButton
                          key={notification.id}
                          onClick={() => {
                            dispatch(setNotificationAsRead(notification.id))
                            setAnchorEl(null)
                          }}
                        >
                          <Box display="flex" my={1} gap={1} flexDirection="row" width="100%">
                            <Box display="flex" pr={2} alignItems="center" sx={theme => ({ color: theme.palette.grey[200] })}>
                              {typeDetail(notification.data.type)}
                            </Box>
                            <Box display="flex" flexDirection="column" width="100%">
                              <Typography variant="body2" color="textSecondary">
                                {notification.data.description}
                              </Typography>
                              {notification.read_at ? (
                                <Typography variant="subtitle1" style={{ fontWeight: 500, lineHeight: '27px' }}>
                                  {notification.data.title}
                                </Typography>
                              ) : (
                                <StyledBadge color="primary" variant="dot">
                                  <Typography variant="subtitle1" style={{ fontWeight: 500, lineHeight: '27px' }}>
                                    {notification.data.title}
                                  </Typography>
                                </StyledBadge>
                              )}
                              <Typography
                                variant="caption"
                                color="textSecondary"
                                style={{ marginTop: '-4px' }}
                              >{`${moment(notification.created_at).fromNow()}`}</Typography>
                            </Box>
                          </Box>
                        </NotificationListItemButton>
                      ))}
                    </NotificationList>
                  </>
                )}
              </TabPanel>

              <TabPanel data-testid="all-notifications" value={currentTab} index={1} noPadding={true}>
                {notifications.length === 0 ? (
                  <Box width="100%" gap={3} height={300} display="flex" alignItems="center" justifyContent="center" flexDirection="column">
                    <BubbleChart style={{ fontSize: '100px' }} htmlColor={`${import.meta.env.VITE_COLOR_PRIMARY_LIGHT || '#FD7296'}`} />
                    <Typography variant="subtitle1">{t('you-havent-notifications')}</Typography>
                    <Button variant="contained" size="small" color="primary" onClick={() => setAnchorEl(null)}>
                      {t('back')}
                    </Button>
                  </Box>
                ) : (
                  <>
                    <Box width="100%" display="flex" justifyContent="flex-end" />
                    <NotificationList>
                      {notifications.map((notification: any) => (
                        <NotificationListItemButton
                          key={notification.id}
                          onClick={() => {
                            dispatch(setNotificationAsRead(notification.id))
                            setAnchorEl(null)
                          }}
                        >
                          <Box display="flex" my={1} gap={1} flexDirection="row" width="100%">
                            <Box display="flex" pr={2} alignItems="center" sx={theme => ({ color: theme.palette.grey[200] })}>
                              {typeDetail(notification.data.type)}
                            </Box>
                            <Box display="flex" flexDirection="column" width="100%">
                              <Typography variant="body2" color="textSecondary">
                                {notification.data.description}
                              </Typography>
                              {notification.read_at ? (
                                <Typography variant="subtitle1" style={{ fontWeight: 500, lineHeight: '27px' }}>
                                  {notification.data.title}
                                </Typography>
                              ) : (
                                <StyledBadge color="primary" variant="dot">
                                  <Typography variant="subtitle1" style={{ fontWeight: 500, lineHeight: '27px' }}>
                                    {notification.data.title}
                                  </Typography>
                                </StyledBadge>
                              )}
                              <Typography
                                variant="caption"
                                color="textSecondary"
                                style={{ marginTop: '-4px' }}
                              >{`${moment(notification.created_at).fromNow()}`}</Typography>
                            </Box>
                          </Box>
                        </NotificationListItemButton>
                      ))}
                    </NotificationList>
                  </>
                )}
              </TabPanel>
            </Box>
          )}
        </Menu>
      </HiddenMobile>

      <HiddenDesktop sx={{ display: 'flex' }}>
        <ButtonNavbar className={theme} onClick={() => setMobileUserOpen(!mobileUserOpen)}>
          <Badge
            badgeContent={notifications.filter((notification: any) => notification.read_at === null).length}
            max={99}
            anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
            color={theme === 'client' ? 'primary' : 'secondary'}
          >
            <NotificationsOutlined />
          </Badge>
        </ButtonNavbar>
        <NotificationDrawerMobile
          variant="temporary"
          anchor="right"
          open={mobileUserOpen}
          onClose={() => setMobileUserOpen(!mobileUserOpen)}
          classes={{ paper: 'client' }}
          ModalProps={{ keepMounted: true }}
        >
          <Box p={2} maxHeight="97vh">
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Typography variant="h6" style={{ fontWeight: 500 }}>
                {t('admin.people.notifications')}
              </Typography>
              <Button onClick={() => setMobileUserOpen(!mobileUserOpen)} variant="text" sx={theme => ({ color: theme.palette.grey[200] })}>
                <CloseRounded />
              </Button>
            </Box>

            <Box>
              <Tabs
                value={currentTab}
                indicatorColor="primary"
                textColor="primary"
                onChange={(_, value) => handleChangeTab(value)}
                style={{ minHeight: 'fit-content' }}
                aria-label="disabled tabs example"
              >
                <NotificationTab label={t('admin.people.pending')} {...a11yProps(0)} />
                <NotificationTab label={t('all')} {...a11yProps(1)} />
              </Tabs>
            </Box>

            <TabPanel value={currentTab} index={0} noPadding={true}>
              {notifications.length === 0 ? (
                <Box width="100%" gap={3} height="80vh" display="flex" alignItems="center" justifyContent="center" flexDirection="column">
                  <BubbleChart style={{ fontSize: '100px' }} htmlColor={`${import.meta.env.VITE_COLOR_PRIMARY_LIGHT || '#FD7296'}`} />
                  <Typography variant="subtitle1">{t('you-havent-notifications')}</Typography>
                  <Button variant="contained" size="small" color="primary" onClick={() => setMobileUserOpen(!mobileUserOpen)}>
                    {t('back')}
                  </Button>
                </Box>
              ) : (
                <>
                  <Box width="100%" display="flex" justifyContent="flex-end">
                    <CleanButton variant="text" size="large">
                      {t('mark-all-as-read')}
                    </CleanButton>
                  </Box>
                  <NotificationList>
                    {notifications.map((notification: any) => (
                      <NotificationListItemButton
                        key={notification.id}
                        onClick={() => {
                          handleClickNotification(notification)
                        }}
                      >
                        <Box display="flex" my={1} gap={1} flexDirection="row" width="100%">
                          <Box display="flex" pr={2} alignItems="center" sx={theme => ({ color: theme.palette.grey[200] })}>
                            {typeDetail(notification.data.type)}
                          </Box>
                          <Box display="flex" flexDirection="column" width="100%">
                            <Typography variant="body2" color="textSecondary">
                              {notification.data.description}
                            </Typography>
                            {notification.read_at ? (
                              <Typography variant="subtitle1" style={{ fontWeight: 500, lineHeight: '27px' }}>
                                {notification.data.title}
                              </Typography>
                            ) : (
                              <StyledBadge color="primary" variant="dot">
                                <Typography variant="subtitle1" style={{ fontWeight: 500, lineHeight: '27px' }}>
                                  {notification.data.title}
                                </Typography>
                              </StyledBadge>
                            )}
                            <Typography
                              variant="caption"
                              color="textSecondary"
                              style={{ marginTop: '-4px' }}
                            >{`${moment(notification.created_at).fromNow()}`}</Typography>
                          </Box>
                        </Box>
                      </NotificationListItemButton>
                    ))}
                  </NotificationList>
                </>
              )}
            </TabPanel>

            <TabPanel value={currentTab} index={1} noPadding={true}>
              {notifications.length === 0 ? (
                <Box width="100%" gap={20} height="80vh" display="flex" alignItems="center" justifyContent="center" flexDirection="column">
                  <BubbleChart style={{ fontSize: '100px' }} htmlColor={`${import.meta.env.VITE_COLOR_PRIMARY_LIGHT || '#FD7296'}`} />
                  <Typography variant="subtitle1">{t('you-havent-notifications')}</Typography>
                  <Button variant="contained" size="small" color="primary" onClick={() => setMobileUserOpen(!mobileUserOpen)}>
                    {t('back')}
                  </Button>
                </Box>
              ) : (
                <>
                  <Box width="100%" display="flex" justifyContent="flex-end" />
                  <NotificationList>
                    {notifications.map((notification: any) => (
                      <NotificationListItemButton
                        key={notification.id}
                        onClick={() => {
                          handleClickNotification(notification)
                        }}
                      >
                        <Box display="flex" my={1} gap={8} flexDirection="row" width="100%">
                          <Box display="flex" pr={2} alignItems="center" sx={theme => ({ color: theme.palette.grey[200] })}>
                            {typeDetail(notification.data.type)}
                          </Box>
                          <Box display="flex" flexDirection="column" width="100%">
                            <Typography variant="body2" color="textSecondary">
                              {notification.data.description}
                            </Typography>
                            {notification.read_at ? (
                              <Typography variant="subtitle1" style={{ fontWeight: 500, lineHeight: '27px' }}>
                                {notification.data.title}
                              </Typography>
                            ) : (
                              <StyledBadge color="primary" variant="dot">
                                <Typography variant="subtitle1" style={{ fontWeight: 500, lineHeight: '27px' }}>
                                  {notification.data.title}
                                </Typography>
                              </StyledBadge>
                            )}
                            <Typography
                              variant="caption"
                              color="textSecondary"
                              style={{ marginTop: '-4px' }}
                            >{`${moment(notification.created_at).fromNow()}`}</Typography>
                          </Box>
                        </Box>
                      </NotificationListItemButton>
                    ))}
                  </NotificationList>
                </>
              )}
            </TabPanel>
          </Box>
        </NotificationDrawerMobile>
      </HiddenDesktop>

      {/* <ScreenLoading loadings={[loading]} /> */}
    </>
  )
}

export default NotificationPanel
